/**
 * Link / embed dialog (#932).
 *
 * Two modes:
 *  - asset mode (no conversionKey): a labeled temporary download link for
 *    the ORIGINAL file, permanent resized CDN links for images, and the
 *    asset's public video transcodes with embed codes.
 *  - conversion mode (conversionKey set, opened from the Formats tab):
 *    everything scoped to that one format — the temporary download link
 *    downloads the conversion, and permanent links are the named-conversion
 *    CDN URLs (`/conversions/{key}?resize=...`, images only).
 *
 * Permanent links only exist for PUBLIC conversions; private ones show a
 * pointer to the Public switch instead.
 */
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useAuthUser } from 'react-auth-kit';
import ReactTooltip from 'react-tooltip';
import BaseModal from 'src/components/BaseModal/BaseModal';
import { useAssetDetailsQuery } from 'src/api/queries/assetsQueries';
import { getAssetLink } from 'src/api/functions/assetsApi';
import { useAccessToken } from 'src/hooks/useAccessToken';
import { useAlerts } from 'src/common/AlertManager';
import { Copy, CodeSimple, AssetGetLink } from 'src/common/icons';
import {
  buildIframeEmbedSnippet,
  buildImgEmbedSnippet,
  getPublicImageConversionUrl,
  getPublicNamedConversionUrl,
  getPublicTranscodeUrl,
} from 'src/utils/previews';
import {
  humanizeConversionName,
  isImageConversion,
  isVideoConversion,
} from 'src/utils/conversionLabels';
import { Asset } from 'src/types/TAssets';
import messages from './messages';
import styles from './AssetLinkDialog.module.scss';

const SIGNED_LINK_EXPIRY_SECONDS = 86400; // 24h

type AssetLinkDialogProps = {
  assetId: string;
  assetName?: string;
  /** Formats-tab entry point: scope every link to this conversion. */
  conversionKey?: string;
};

type SizePreset = { key: 'small' | 'medium' | 'large'; width: number; height: number };

const SIZE_PRESETS: SizePreset[] = [
  { key: 'small', width: 480, height: 480 },
  { key: 'medium', width: 1024, height: 1024 },
  { key: 'large', width: 2048, height: 1536 },
];

export const AssetLinkDialog: React.FC<AssetLinkDialogProps> = ({
  assetId,
  assetName,
  conversionKey,
}) => {
  const { formatMessage } = useIntl();
  const auth = useAuthUser();
  const accessToken = useAccessToken();
  const { showInfo, showAlert } = useAlerts();

  const { data: asset } = useAssetDetailsQuery(assetId) as { data: Asset | undefined };

  const [tempLinkBusy, setTempLinkBusy] = useState(false);
  const [customWidth, setCustomWidth] = useState(800);
  const [customHeight, setCustomHeight] = useState(600);
  const [customFormat, setCustomFormat] = useState('');

  // tooltip contents change as rows re-render (custom size, format)
  useEffect(() => {
    ReactTooltip.rebuild();
  });

  const conversion = conversionKey ? asset?.conversions?.[conversionKey] : undefined;
  const conversionMode = !!conversionKey;
  const conversionIsPublic = !!conversion?.public;

  // asset mode reads the asset's own type; conversion mode the conversion's
  const showImageLinks = conversionMode
    ? !!conversion && isImageConversion(conversionKey!, conversion)
    : !!asset?.content_type?.startsWith('image/');
  const showVideoLinks = conversionMode
    ? !!conversion && isVideoConversion(conversionKey!, conversion)
    : !!asset?.content_type?.startsWith('video/');

  const copyText = async (text: string, confirmation: string) => {
    try {
      await navigator.clipboard.writeText(text);
      showInfo(confirmation);
    } catch (error) {
      showAlert(formatMessage(messages.copyFailed));
    }
  };

  const copyTempLink = async () => {
    setTempLinkBusy(true);
    try {
      const url = await getAssetLink(
        assetId,
        auth()!.accessId,
        conversionKey || 'original',
        SIGNED_LINK_EXPIRY_SECONDS,
        'download',
        accessToken,
      );
      await copyText(url, formatMessage(messages.tempLinkCopied));
    } catch (error) {
      showAlert(formatMessage(messages.copyFailed));
    } finally {
      setTempLinkBusy(false);
    }
  };

  const imageUrlFor = (width: number, height: number, format?: string) =>
    getPublicImageConversionUrl({
      id: assetId,
      width,
      height,
      format: format || undefined,
      conversionKey,
    });

  // asset mode lists every public transcode; conversion mode just this one
  const publicVideoConversions: [string, any][] = conversionMode
    ? conversionIsPublic && conversion
      ? [[conversionKey!, conversion]]
      : []
    : Object.entries(asset?.conversions || {}).filter(
        ([key, conv]) => conv.public && isVideoConversion(key, conv),
      );

  const renderCopyRow = (
    label: string,
    url: string,
    meta?: string,
    snippet?: { text: string; tip: string; copied: string },
    key?: string,
  ) => (
    <div className={styles.linkRow} key={key || label}>
      <span className={styles.rowLabel}>{label}</span>
      {meta && <span className={styles.rowMeta}>{meta}</span>}
      <span className={styles.rowUrl}>{url}</span>
      <span className={styles.rowActions}>
        <button
          type="button"
          className={styles.iconButton}
          onClick={() => copyText(url, formatMessage(messages.permanentLinkCopied))}
          data-tip={formatMessage(messages.copyUrl)}
        >
          <Copy />
        </button>
        {snippet && (
          <button
            type="button"
            className={styles.iconButton}
            onClick={() => copyText(snippet.text, snippet.copied)}
            data-tip={snippet.tip}
          >
            <CodeSimple />
          </button>
        )}
      </span>
    </div>
  );

  const renderImageRow = (label: string, meta: string, width: number, height: number, format?: string) => {
    const url = imageUrlFor(width, height, format);
    return renderCopyRow(
      label,
      url,
      meta,
      {
        text: buildImgEmbedSnippet(url, assetName || asset?.name || ''),
        tip: formatMessage(messages.copyImgSnippet),
        copied: formatMessage(messages.imgSnippetCopied),
      },
      `${label}-${width}x${height}-${format || 'auto'}`,
    );
  };

  // private conversions have no permanent CDN link at all
  const showPermanentSections = !conversionMode || conversionIsPublic;

  return (
    <BaseModal containerStyle={{ width: 'min(640px, 94vw)' }}>
      {/* one tooltip instance for the dialog — a per-button instance renders a
          duplicate "shadow" tooltip box behind the visible one */}
      <ReactTooltip effect="solid" />
      <div className={styles.dialog}>
        <h3>{formatMessage(messages.title)}</h3>
        {conversionMode && (
          <p className={styles.hint}>
            {formatMessage(messages.conversionSubtitle, {
              name: humanizeConversionName(conversionKey!),
              asset: assetName || asset?.name || assetId,
            })}
          </p>
        )}

        <div className={styles.section}>
          <h4>
            {formatMessage(conversionMode ? messages.tempSectionTitleConversion : messages.tempSectionTitle)}
          </h4>
          <p className={styles.hint}>
            {formatMessage(conversionMode ? messages.tempSectionHintConversion : messages.tempSectionHint)}
          </p>
          <div className={styles.linkRow}>
            <span className={styles.rowLabel}>
              {conversionMode ? humanizeConversionName(conversionKey!) : assetName || asset?.name || assetId}
            </span>
            <span className={styles.rowUrl} />
            <span className={styles.rowActions}>
              <button
                type="button"
                className={styles.iconButton}
                disabled={tempLinkBusy}
                onClick={copyTempLink}
                data-tip={formatMessage(messages.copyTempLink)}
              >
                <AssetGetLink loading={tempLinkBusy} />
              </button>
            </span>
          </div>
        </div>

        {conversionMode && !conversionIsPublic && (
          <div className={styles.emptyHint}>{formatMessage(messages.conversionNotPublic)}</div>
        )}

        {/* conversion mode: the stored file as-is, at its own size */}
        {conversionMode && showPermanentSections && !showVideoLinks && (
          <div className={styles.section}>
            <h4>{formatMessage(messages.directSectionTitle)}</h4>
            <p className={styles.hint}>{formatMessage(messages.directSectionHint)}</p>
            {renderCopyRow(
              formatMessage(messages.fullSize),
              getPublicNamedConversionUrl(assetId, conversionKey!),
            )}
          </div>
        )}

        {showImageLinks && showPermanentSections && (
          <div className={styles.section}>
            <h4>{formatMessage(messages.imageSectionTitle)}</h4>
            <p className={styles.hint}>
              {formatMessage(conversionMode ? messages.imageSectionHintConversion : messages.imageSectionHint)}
            </p>
            {SIZE_PRESETS.map((preset) =>
              renderImageRow(
                formatMessage(
                  preset.key === 'small'
                    ? messages.sizeSmall
                    : preset.key === 'medium'
                    ? messages.sizeMedium
                    : messages.sizeLarge,
                ),
                `${preset.width}×${preset.height}`,
                preset.width,
                preset.height,
              ),
            )}
            <div className={styles.customRow}>
              <label>
                {formatMessage(messages.widthLabel)}
                <input
                  type="number"
                  min={16}
                  max={2048}
                  value={customWidth}
                  onChange={(e) => setCustomWidth(Number(e.target.value) || 0)}
                />
              </label>
              <label>
                {formatMessage(messages.heightLabel)}
                <input
                  type="number"
                  min={16}
                  max={1536}
                  value={customHeight}
                  onChange={(e) => setCustomHeight(Number(e.target.value) || 0)}
                />
              </label>
              <label>
                {formatMessage(messages.formatLabel)}
                <select value={customFormat} onChange={(e) => setCustomFormat(e.target.value)}>
                  <option value="">{formatMessage(messages.formatAuto)}</option>
                  <option value="jpg">JPG</option>
                  <option value="png">PNG</option>
                  <option value="webp">WebP</option>
                </select>
              </label>
            </div>
            {customWidth >= 16 &&
              customHeight >= 16 &&
              renderImageRow(
                formatMessage(messages.sizeCustom),
                `${Math.min(customWidth, 2048)}×${Math.min(customHeight, 1536)}`,
                customWidth,
                customHeight,
                customFormat,
              )}
          </div>
        )}

        {showVideoLinks && showPermanentSections && (
          <div className={styles.section}>
            <h4>{formatMessage(messages.videoSectionTitle)}</h4>
            <p className={styles.hint}>{formatMessage(messages.videoSectionHint)}</p>
            {publicVideoConversions.length === 0 ? (
              <div className={styles.emptyHint}>{formatMessage(messages.noPublicVideos)}</div>
            ) : (
              publicVideoConversions.map(([key]) =>
                renderCopyRow(
                  humanizeConversionName(key),
                  getPublicTranscodeUrl(assetId, key),
                  undefined,
                  {
                    text: buildIframeEmbedSnippet(assetId, key),
                    tip: formatMessage(messages.copyIframeSnippet),
                    copied: formatMessage(messages.iframeSnippetCopied),
                  },
                  key,
                ),
              )
            )}
          </div>
        )}
      </div>
    </BaseModal>
  );
};

export default AssetLinkDialog;
